import React, { useState, useEffect, useMemo } from 'react';

// Stats panel sin dependencias externas — line chart SVG inline + KPIs.
// Rango: presets (7/30/90/180/365d) O fechas libres (desde/hasta).
// Por subtab (Surveys|Tareas) se muestra UN chart combinado compacto.

const COLORS = {
  surveys_created: '#3b82f6',  // azul
  surveys_edited:  '#22c55e',  // verde
  tasks_created:   '#f59e0b',  // ambar
  tasks_edited:    '#ef4444',  // rojo
};

const PRESETS = [7, 30, 90, 180, 365];

export default function ClientUsageCharts({ clientId }) {
  const [preset, setPreset] = useState(30);            // dias, o null si custom
  const [from, setFrom]     = useState(isoDaysAgo(30));
  const [to, setTo]         = useState(isoToday());
  const [stats, setStats]   = useState(null);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [subtab, setSubtab] = useState('surveys');

  // Aplicar un preset -> recalcula from/to
  const applyPreset = (d) => {
    setPreset(d);
    setFrom(isoDaysAgo(d));
    setTo(isoToday());
  };

  useEffect(() => {
    let cancel = false;
    setLoad(true);
    const url = `/settings/clients_v2/${clientId}/usage_stats?from=${from}&to=${to}`;
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(j => { if (!cancel) { setStats(j); setLoad(false); } })
      .catch(e => { if (!cancel) { setError(e.message); setLoad(false); } });
    return () => { cancel = true; };
  }, [clientId, from, to]);

  if (loading) return <div className="cv2-loading"><i className="fas fa-spinner fa-spin" /> Cargando estadísticas…</div>;
  if (error)   return <div className="cv2-error">Error: {error}</div>;
  if (!stats)  return null;

  return (
    <div className="cv2-stats">
      <div className="cv2-stats__header">
        <div>
          <h4 className="cv2-panel-title">Actividad</h4>
          <p className="cv2-stats__period">{stats.from} → {stats.to} · {stats.days} días</p>
        </div>
        <div className="cv2-range-controls">
          <div className="cv2-range-tabs">
            {PRESETS.map(r => (
              <button
                key={r}
                className={`cv2-range-btn ${preset === r ? 'cv2-range-btn--active' : ''}`}
                onClick={() => applyPreset(r)}
              >{r}d</button>
            ))}
          </div>
          <div className="cv2-daterange">
            <input type="date" value={from} max={to}
                   onChange={e => { setPreset(null); setFrom(e.target.value); }} />
            <span>→</span>
            <input type="date" value={to} min={from} max={isoToday()}
                   onChange={e => { setPreset(null); setTo(e.target.value); }} />
          </div>
        </div>
      </div>

      <KpiGrid totals={stats.totals} days={stats.days} />

      <div className="cv2-subtabs">
        <button
          className={`cv2-subtab ${subtab === 'surveys' ? 'cv2-subtab--active' : ''}`}
          onClick={() => setSubtab('surveys')}
        >
          <i className="fas fa-clipboard-list" /> Surveys
        </button>
        <button
          className={`cv2-subtab ${subtab === 'tasks' ? 'cv2-subtab--active' : ''}`}
          onClick={() => setSubtab('tasks')}
        >
          <i className="fas fa-tasks" /> Tareas
        </button>
      </div>

      {subtab === 'surveys' && (
        <GroupChart
          series={stats.series}
          createdKey="surveys_created" editedKey="surveys_edited"
          createdLabel="Creados" editedLabel="Editados"
          title="Surveys (creados + editados)"
        />
      )}
      {subtab === 'tasks' && (
        <GroupChart
          series={stats.series}
          createdKey="tasks_created" editedKey="tasks_edited"
          createdLabel="Creadas" editedLabel="Editadas"
          title="Tareas (creadas + editadas)"
        />
      )}

      <div className="cv2-card">
        <h5>Top 5 días con mayor actividad</h5>
        <TopDaysTable items={stats.top_days} />
      </div>
    </div>
  );
}

// UN solo chart combinado por grupo (compacto). Totales como chips en el head.
function GroupChart({ series, createdKey, editedKey, createdLabel, editedLabel, title }) {
  const createdColor = COLORS[createdKey];
  const editedColor  = COLORS[editedKey];
  const totalCreated = series.reduce((s, p) => s + (p[createdKey] || 0), 0);
  const totalEdited  = series.reduce((s, p) => s + (p[editedKey]  || 0), 0);

  return (
    <div className="cv2-card">
      <div className="cv2-chart-head">
        <h5>{title}</h5>
        <div className="cv2-chip-totals">
          <span><i style={{ background: createdColor }} /> {createdLabel}: <strong>{totalCreated.toLocaleString()}</strong></span>
          <span><i style={{ background: editedColor }} /> {editedLabel}: <strong>{totalEdited.toLocaleString()}</strong></span>
        </div>
      </div>
      <LineChart
        series={series}
        lines={[
          { key: createdKey, color: createdColor, label: createdLabel },
          { key: editedKey,  color: editedColor,  label: editedLabel  },
        ]}
      />
    </div>
  );
}

function KpiGrid({ totals, days }) {
  const items = [
    { label: 'Surveys creados',  value: totals.surveys_created, color: COLORS.surveys_created, icon: 'fa-plus-circle' },
    { label: 'Surveys editados', value: totals.surveys_edited,  color: COLORS.surveys_edited,  icon: 'fa-edit' },
    { label: 'Tasks creadas',    value: totals.tasks_created,   color: COLORS.tasks_created,   icon: 'fa-tasks' },
    { label: 'Tasks editadas',   value: totals.tasks_edited,    color: COLORS.tasks_edited,    icon: 'fa-pencil-alt' },
  ];
  const avg = days > 0 ? Math.round((totals.total || 0) / days * 10) / 10 : 0;

  return (
    <div className="cv2-kpi-grid">
      {items.map(it => (
        <div key={it.label} className="cv2-kpi" style={{ borderTopColor: it.color }}>
          <i className={`fas ${it.icon}`} style={{ color: it.color }} />
          <div className="cv2-kpi__value">{(it.value || 0).toLocaleString()}</div>
          <div className="cv2-kpi__label">{it.label}</div>
        </div>
      ))}
      <div className="cv2-kpi cv2-kpi--total">
        <i className="fas fa-chart-line" />
        <div className="cv2-kpi__value">{(totals.total || 0).toLocaleString()}</div>
        <div className="cv2-kpi__label">Total · prom {avg}/día</div>
      </div>
    </div>
  );
}

// LineChart generico compacto: dibuja N lineas. Cada line { key, color, label }
function LineChart({ series, lines, height = 170 }) {
  const W = 720;
  const H = height;
  const PAD = { top: 10, right: 12, bottom: 22, left: 34 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top  - PAD.bottom;

  const data = useMemo(() => series, [series]);

  if (!data.length) {
    return <div className="cv2-empty-state"><p>Sin datos de actividad en este rango.</p></div>;
  }

  const maxY = Math.max(1, ...data.flatMap(d => lines.map(l => d[l.key] || 0)));
  const xAt = (i) => PAD.left + (data.length <= 1 ? 0 : (i / (data.length - 1)) * innerW);
  const yAt = (v) => PAD.top  + innerH - (v / maxY) * innerH;

  const yTicks   = [0, 0.5, 1].map(p => Math.round(maxY * p));
  const xTickStep = Math.max(1, Math.floor(data.length / 6));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="cv2-linechart" preserveAspectRatio="xMidYMid meet">
      {yTicks.map((tv, i) => {
        const y = yAt(tv);
        return (
          <g key={`y-${i}`}>
            <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#eef0f3" strokeWidth="1" />
            <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize="10" fill="#94a3b8">{tv}</text>
          </g>
        );
      })}

      {data.map((d, i) => {
        if (i % xTickStep !== 0 && i !== data.length - 1) return null;
        return (
          <text key={`x-${d.date}`} x={xAt(i)} y={H - 6} textAnchor="middle" fontSize="9" fill="#94a3b8">
            {d.date.slice(5)}
          </text>
        );
      })}

      {lines.map(line => {
        const path = data
          .map((d, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(d[line.key] || 0)}`)
          .join(' ');
        return (
          <g key={line.key}>
            <path d={path} fill="none" stroke={line.color} strokeWidth="2" />
            {data.map((d, i) => {
              const v = d[line.key] || 0;
              return (
                <g key={`${line.key}-${i}`} className="cv2-chart-point">
                  <circle cx={xAt(i)} cy={yAt(v)} r="2.5" fill={line.color} />
                  <circle cx={xAt(i)} cy={yAt(v)} r="10" fill="transparent">
                    <title>{`${d.date}: ${v.toLocaleString()} ${line.label}`}</title>
                  </circle>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

function TopDaysTable({ items }) {
  if (!items?.length) return <p className="cv2-empty-inline">Sin días con actividad.</p>;
  return (
    <table className="cv2-mini-table">
      <thead>
        <tr><th>Fecha</th><th className="cv2-td--num">Actividad</th></tr>
      </thead>
      <tbody>
        {items.map(it => (
          <tr key={it.date}>
            <td>{it.date}</td>
            <td className="cv2-td--num">{it.total.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function isoToday() {
  return localISO(new Date());
}
function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localISO(d);
}
function localISO(d) {
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}
